import { useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, ClickAwayListener, Icon, ProgressBar, Typography } from 'shared/ui'
import { DayjsFormats, numberFormat } from 'shared/lib'
import localDate from 'shared/lib/dayjs'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AutoRechargeCard } from 'entities/Billing/ui/AutoRechargeWidget/ui/AutoRechargeCard'
import { TooltipCollapse } from 'pages/settings/pages/billing/pages/overview/ui/CreditsInfo/TooltipCollapse'
import { CreditsInfoStore } from './store/creditsInfoStore'
import styles from './styles.module.scss'

export const CreditsInfo = observer(() => {
  const { subscriptionStore, organizationStore, billingStore } = useEntitiesContext()
  const store = useMemo(
    () => new CreditsInfoStore(subscriptionStore, organizationStore, billingStore),
    []
  )
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const isTrial = subscriptionStore.isTrial

  const accountCreditsRaw = organizationStore.accountCredits
  const rolloverCreditsRaw = organizationStore.rolloverCredits
  const purchasedCreditsRaw = organizationStore.purchasedCredits || 0
  const planCreditsRaw = subscriptionStore.planCredits ?? 0
  const isRollover = rolloverCreditsRaw > 0

  const totalCredits = rolloverCreditsRaw + purchasedCreditsRaw + planCreditsRaw

  const creditsPercent = totalCredits > 0 ? Math.round((accountCreditsRaw / totalCredits) * 100) : 0

  const accountCredits = numberFormat({ value: accountCreditsRaw })
  const trialCredits = numberFormat({ value: organizationStore.trialCredits })
  const rolloverCredits = numberFormat({ value: rolloverCreditsRaw })
  const purchasedCredits = numberFormat({ value: purchasedCreditsRaw })
  const planCredits = numberFormat({ value: planCreditsRaw })
  const credits = isTrial ? trialCredits : accountCredits
  const periodEndsAt = localDate(subscriptionStore.periodEndsAt).format(DayjsFormats.full6)
  const showProgressBar = !isTrial && !subscriptionStore.isPAYGPlan

  const collapseItems = [
    {
      credits: planCredits,
      title: 'Plan',
      content: (
        <>
          Credits included with your plan.
          <br />
          Next reset on {periodEndsAt}.
        </>
      ),
    },
    {
      credits: purchasedCredits,
      title: 'Purchased',
      content: (
        <>
          Credits bought manually or through auto-recharge.
          <br />
          Expire in 12 months from purchase date.
        </>
      ),
    },
    ...(isRollover
      ? [
          {
            credits: rolloverCredits,
            title: 'Rollover',
            content: (
              <>
                Unused plan credits from last billing cycle.
                <br />
                {organizationStore.rolloverCreditsRemoveDate
                  ? `Expire on ${localDate(organizationStore.rolloverCreditsRemoveDate).format(
                      DayjsFormats.full6
                    )}.`
                  : "Don't expire."}
              </>
            ),
          },
        ]
      : []),
  ]

  const tooltipContent = (
    <div className={styles.tooltipContainer}>
      {collapseItems.map((item) => (
        <TooltipCollapse
          key={item.title}
          credits={item.credits}
          title={item.title}
          content={item.content}
        />
      ))}
    </div>
  )

  const handleTooltipOpen = () => {
    setIsTooltipOpen(true)
  }
  const handleTooltipClose = () => {
    setIsTooltipOpen(false)
  }

  return (
    <div className={styles.creditsContainer}>
      <div className={styles.creditsHeaderContainer}>
        <div className={styles.creditsHeader}>
          <div className={styles.leftContent}>
            <Icon icon='credits' fontSize={20} color='var(--content-brand-primary)' />
            <Typography ariaLabel='available_credits' variant='body-lg-medium'>
              Available credits: {credits}
            </Typography>
          </div>
          <div className={styles.rightContent}>
            <Button
              text='Credit usage'
              typeBtn='text'
              size='medium'
              color='var(--light-blue-60)'
              onClick={store.onCreditUsageClick}
            />
            <Button
              text='Buy credits'
              contained='tertiary'
              size='medium'
              onClick={store.onBuyCreditsClick}
            />
          </div>
        </div>
        {showProgressBar && (
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <div>
              <ProgressBar
                percent={creditsPercent}
                withAllBorders
                withBg
                tooltipProps={{
                  open: isTooltipOpen,
                  disableFocusListener: true,
                  disableTouchListener: true,
                  onOpen: handleTooltipOpen,
                  label: tooltipContent,
                  placement: 'bottom',
                  TransitionProps: { timeout: { exit: 0 } },
                }}
              />
            </div>
          </ClickAwayListener>
        )}
      </div>
      <AutoRechargeCard
        store={store.autoRechargeWidgetStore}
        className={showProgressBar ? styles.autoRechargeCard : styles.autoRechargeCardNoProgressBar}
        onSwitchChange={store.onAutoRechargeSwitchChange}
        onEditThresholdClick={store.onAutoRechargeEditThresholdClick}
      />
    </div>
  )
})
